import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserAvatar } from '@t-one-internal/design-system';

interface TeamMember {
    userId: string | number;
    employeeName: string;
    jobTitle: string;
    avgManagerRating: number;
    latestCycleName: string;
}

interface MyTeamListProps {
    data: Array<Record<string, unknown>>;
}

const toTeamMember = (raw: Record<string, unknown>): TeamMember => ({
    userId: (raw.userId as string | number) ?? '',
    employeeName: (raw.employeeName as string) ?? 'Unknown',
    jobTitle: (raw.jobTitle as string) ?? '',
    avgManagerRating: Number(raw.avgManagerRating ?? 0),
    latestCycleName: (raw.latestCycleName as string) ?? '',
});

const getRatingColor = (rating: number): string => {
    if (rating >= 4) return 'text-emerald-600';
    if (rating >= 3) return 'text-amber-600';
    if (rating > 0) return 'text-red-600';
    return 'text-gray-400';
};

export const MyTeamList: React.FC<MyTeamListProps> = ({ data }) => {
    const navigate = useNavigate();
    const items = useMemo(() => {
        if (!data || !Array.isArray(data)) return [];
        return data.map(toTeamMember);
    }, [data]);

    if (items.length === 0) {
        return (
            <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                No direct reports found
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full overflow-hidden">
            <div className="flex-1 overflow-auto min-h-0 space-y-1" style={{ scrollbarWidth: 'none' }}>
                {items.map((member, index) => (
                    <div
                        key={String(member.userId) + index}
                        onClick={() => navigate(`/workforce/employee/${String(member.userId)}`)}
                        className="flex items-center gap-3 px-2 py-2 hover:bg-gray-50/80 rounded-lg transition-colors cursor-pointer"
                    >
                        <div className="shrink-0">
                            <UserAvatar
                                name={member.employeeName}
                                userId={member.userId}
                                size="lg"
                            />
                        </div>

                        <div className="flex-1 min-w-0">
                            <div className="text-[13px] font-semibold text-gray-900 truncate leading-tight">
                                {member.employeeName}
                            </div>
                            <div className="text-[11px] text-gray-400 truncate mt-0.5">
                                {member.jobTitle || 'No title'}
                                {member.latestCycleName && (
                                    <> · {member.latestCycleName}</>
                                )}
                            </div>
                        </div>

                        <div className="shrink-0 text-right">
                            <div className={`text-sm font-bold leading-tight ${getRatingColor(member.avgManagerRating)}`}>
                                {member.avgManagerRating > 0
                                    ? member.avgManagerRating.toFixed(1)
                                    : '—'}
                            </div>
                            <div className="text-[10px] text-gray-400 leading-tight">Rating</div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};
